import L from "leaflet";
import type { LatLngTuple } from "../core/options";
import type { Tool, ToolHost } from "./host";

export function pinIcon(touch: boolean): L.DivIcon {
  const size = 36;
  const hit = touch ? 48 : size;

  return L.divIcon({
    className: "fxmap-draw-pin-hit",
    html: `<span class="fxmap-draw-pin" data-fxmap="draw-pin"></span>`,
    iconSize: [hit, hit],
    iconAnchor: [hit / 2, hit - (hit - size) / 2 - 2]
  });
}

export class PointTool implements Tool {
  readonly mode = "point" as const;

  private readonly host: ToolHost;
  private marker: L.Marker | null = null;
  private editable: boolean;

  constructor(host: ToolHost) {
    this.host = host;
    this.editable = host.editable();
  }

  render(points: LatLngTuple[]): void {
    const point = points.length > 0 ? points[0] : null;

    if (point === null) {
      this.marker?.remove();
      this.marker = null;
      return;
    }

    if (this.marker === null) {
      this.marker = L.marker(point, {
        icon: pinIcon(this.host.host.touch),
        draggable: this.editable,
        keyboard: false,
        autoPan: true,
        pane: this.host.pane,
        zIndexOffset: 1000
      }).addTo(this.host.host.map);

      this.marker.on("drag", event => {
        const position = (event.target as L.Marker).getLatLng();

        this.host.preview([[position.lat, position.lng]]);
      });

      this.marker.on("dragend", () => {
        const position = this.marker?.getLatLng();

        if (position === undefined) return;

        const snapped = this.host.snapper.snap([position.lat, position.lng], false);

        this.host.commit([snapped.point]);
      });

      this.marker.on("click", event => L.DomEvent.stopPropagation(event));
      return;
    }

    const current = this.marker.getLatLng();

    if (current.lat !== point[0] || current.lng !== point[1]) this.marker.setLatLng(point);
  }

  onMapClick(point: LatLngTuple): void {
    if (!this.editable) return;

    this.host.commit([point]);
    this.host.setPhase("editing");
  }

  onMapMouseMove(): void {
    return;
  }

  onDoubleClick(): void {
    return;
  }

  removeLast(): void {
    if (this.editable) this.host.commit([]);
  }

  deleteSelectedVertex(): void {
    this.removeLast();
  }

  finishGesture(): void {
    return;
  }

  setEditable(on: boolean): void {
    this.editable = on;

    if (this.marker === null) return;

    if (on) this.marker.dragging?.enable();
    else this.marker.dragging?.disable();
  }

  dispose(): void {
    this.marker?.remove();
    this.marker = null;
  }
}
